/**
 * The scene lane: clips positioned by their resolved cues, with a
 * transition chip on every boundary.
 */

import { useState } from 'react';
import type { ProjectPage, SceneCue, VideoScene } from '@canvas/shared-types';
import SceneClip from './SceneClip';
import TransitionChip from './TransitionChip';

interface SceneStripProps {
  pages: ProjectPage[];
  scenes: VideoScene[];
  cues: SceneCue[];
  pxPerSec: number;
  activePageId: string;
  livePageId: string | null;
  thumbnails: Record<string, string>;
  onSelect: (pageId: string) => void;
  onRename: (pageId: string, name: string) => void;
  onReorder: (fromId: string, toId: string) => void;
}

export default function SceneStrip({
  pages,
  scenes,
  cues,
  pxPerSec,
  activePageId,
  livePageId,
  thumbnails,
  onSelect,
  onRename,
  onReorder,
}: SceneStripProps) {
  const [dragId, setDragId] = useState<string | null>(null);
  const pageById = new Map(pages.map((p) => [p.pageId, p]));

  return (
    <div className="vt-lane vt-lane-scenes">
      {scenes.map((scene, i) => {
        const cue = cues[i];
        const page = pageById.get(scene.pageId);
        if (!cue || !page) return null;
        return (
          <SceneClip
            key={scene.pageId}
            page={page}
            scene={scene}
            cue={cue}
            pxPerSec={pxPerSec}
            thumb={thumbnails[scene.pageId]}
            active={scene.pageId === activePageId}
            live={scene.pageId === livePageId}
            dragging={dragId === scene.pageId}
            onSelect={onSelect}
            onRename={onRename}
            onDragStart={setDragId}
            onDragEnd={() => setDragId(null)}
            onDropOn={(targetId, e) => {
              const fromId = e.dataTransfer.getData('text/plain') || dragId;
              if (fromId && fromId !== targetId) onReorder(fromId, targetId);
              setDragId(null);
            }}
          />
        );
      })}
      {scenes.slice(1).map((scene, i) => {
        const cue = cues[i + 1];
        if (!cue) return null;
        const x = ((cue.startMs + cue.overlapInMs / 2) / 1000) * pxPerSec;
        return <TransitionChip key={`t-${scene.pageId}`} scene={scene} x={x} />;
      })}
    </div>
  );
}
